/**
 * Baby Development Page
 *
 * Shows baby development phases and milestones.
 * Displays phase info, milestones checklist, development content,
 * activities, and medical information.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, Ruler, Circle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore, Pregnancy } from '@/store/pregnancyStore';
import { SSWCircle } from '@/components/SSWCircle';
import { useBabyGrowth } from '@/features/baby-growth';
import { calculateBabyAgeParts } from '@/lib/babyAge';
import { useBabyPhase, usePhaseContent, useMilestones, useMilestoneSync } from '../hooks';
import {
  PhaseInfo,
  MilestoneChecklist,
  DevelopmentContent,
  ActivitiesSection,
  MedicalSection,
} from '../components';
import { RecommendationSlot } from '@/features/recommendations';

// Helper to format growth values with appropriate units
function formatGrowthValue(value: number | null | undefined, type: 'weight' | 'length'): { value: string; unit: string } {
  if (value === null || value === undefined) {
    return { value: '-', unit: type === 'weight' ? 'g' : 'cm' };
  }

  if (type === 'weight') {
    // Weight in grams, convert to kg at 8000g
    if (value >= 8000) {
      return { value: (value / 1000).toFixed(2), unit: 'kg' };
    }
    return { value: Math.round(value).toString(), unit: 'g' };
  } else {
    // Length in cm, convert to m at 100cm
    if (value >= 100) {
      return { value: (value / 100).toFixed(2), unit: 'm' };
    }
    return { value: value.toFixed(1), unit: 'cm' };
  }
}

// Baby age circle component (matching dashboard display)
function BabyAgeCircle({ pregnancy }: { pregnancy: Pregnancy }) {
  const { t, i18n } = useTranslation(['babyDevelopment', 'dashboard']);
  const birthDate = pregnancy.actual_birth_date
    ? new Date(pregnancy.actual_birth_date)
    : null;

  const age = birthDate ? calculateBabyAgeParts(birthDate) : { years: 0, months: 0, weeks: 0, days: 0 };

  const babyNames = useMemo(() => {
    if (pregnancy.baby_data?.babies) {
      return pregnancy.baby_data.babies
        .filter(b => b.name)
        .map(b => ({
          name: b.name!,
          gender: (b.gender === 'male' || b.gender === 'female' ? b.gender : 'neutral') as 'male' | 'female' | 'neutral',
        }));
    }
    if (pregnancy.baby_names && Array.isArray(pregnancy.baby_names)) {
      return pregnancy.baby_names.map((n: string | { name: string; gender?: string }) => {
        if (typeof n === 'string') {
          return { name: n, gender: 'neutral' as const };
        }
        const gender = n.gender === 'male' || n.gender === 'female' ? n.gender : 'neutral';
        return { name: n.name, gender: gender as 'male' | 'female' | 'neutral' };
      });
    }
    return [];
  }, [pregnancy.baby_data?.babies, pregnancy.baby_names]);

  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';
  const birthDateFormatted = birthDate
    ? birthDate.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';


  // Build age display: x Jahre, y Monate, z Wochen, w Tage (top to bottom)
  // Only show each unit if > 0
  const getAgeDisplay = () => {
    const parts: string[] = [];

    if (age.years > 0) {
      parts.push(t('dashboard:babyAge.year', { count: age.years }));
    }

    if (age.months > 0) {
      parts.push(t('dashboard:babyAge.month', { count: age.months }));
    }

    if (age.weeks > 0) {
      parts.push(t('dashboard:babyAge.week', { count: age.weeks }));
    }

    if (age.days > 0) {
      parts.push(t('dashboard:babyAge.day', { count: age.days }));
    }

    if (parts.length === 0) {
      return t('dashboard:babyAge.zeroDays');
    }

    return parts.join('\n');
  };

  // Custom content for the circle with counter
  const circleContent = (
    <div className="flex flex-col items-center text-center" style={{ lineHeight: '1.3' }}>
      <p className="text-white/70 text-sm mb-1">
        {t('dashboard:babyAge.since')} <span className="font-bold text-white">{birthDateFormatted}</span>
      </p>
      <div className="text-white text-2xl font-bold whitespace-pre-line">{getAgeDisplay()}</div>
    </div>
  );

  return (
    <SSWCircle
      currentSSW={0}
      dayInWeek={0}
      babyNames={babyNames}
      overrideProgress={100}
      overrideContent={circleContent}
    />
  );
}

export function BabyDevelopmentPage() {
  const { t } = useTranslation(['babyDevelopment', 'dashboard']);
  const navigate = useNavigate();
  const { appMode, lastCompletedPregnancy } = usePregnancyStore();

  // Redirect to /ssw if not in baby mode
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  useEffect(() => {
    if (!isBabyMode) {
      navigate('/ssw', { replace: true });
    }
  }, [isBabyMode, navigate]);

  // Get baby growth data
  const { entries: growthEntries, isLoading: isGrowthLoading } = useBabyGrowth();

  // Get baby phase info
  const phaseInfo = useBabyPhase();

  // Get phase content
  const {
    categories,
    content,
    activities,
    medical,
    isLoading: isContentLoading,
  } = usePhaseContent(phaseInfo?.phase?.id);

  // Get milestones
  const {
    milestones,
    babies,
    isLoading: isMilestonesLoading,
    checkMilestone,
    uncheckMilestone,
    updateNotes,
    isUpdating,
  } = useMilestones(phaseInfo?.phase?.id);

  // Enable real-time sync for milestones
  useMilestoneSync();

  // Find last recorded value for each growth category
  const latestGrowthValues = useMemo(() => ({
    weight: growthEntries.find(e => e.weight_grams !== null)?.weight_grams ?? null,
    height: growthEntries.find(e => e.height_cm !== null)?.height_cm ?? null,
    head: growthEntries.find(e => e.head_circumference_cm !== null)?.head_circumference_cm ?? null,
  }), [growthEntries]);

  // Build page title with baby names
  const pageTitle = useMemo(() => {
    if (!lastCompletedPregnancy) return t('pageTitle.default');

    const names: string[] = [];

    if (lastCompletedPregnancy.baby_data?.babies) {
      lastCompletedPregnancy.baby_data.babies.forEach(b => {
        if (b.name) names.push(b.name);
      });
    } else if (lastCompletedPregnancy.baby_names && Array.isArray(lastCompletedPregnancy.baby_names)) {
      lastCompletedPregnancy.baby_names.forEach((n: string | { name: string }) => {
        if (typeof n === 'string' && n) {
          names.push(n);
        } else if (typeof n === 'object' && n.name) {
          names.push(n.name);
        }
      });
    }

    if (names.length === 0) return t('pageTitle.default');
    if (names.length === 1) return t('pageTitle.ofOne', { name: names[0] });
    if (names.length === 2) return t('pageTitle.ofTwo', { name1: names[0], name2: names[1] });

    // 3+ names: "Name1, Name2 & Name3"
    const lastIndex = names.length - 1;
    return t('pageTitle.ofMany', { names: names.slice(0, lastIndex).join(', '), lastName: names[lastIndex] });
  }, [lastCompletedPregnancy, t]);

  if (!isBabyMode || !lastCompletedPregnancy) {
    return null; // Will redirect
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Page Title */}
      <section className="pb-2">
        <h1 className="text-white text-2xl font-bold">{pageTitle}</h1>
      </section>

      {/* Age Circle */}
      <section className="flex flex-col items-center">
        <BabyAgeCircle pregnancy={lastCompletedPregnancy} />

        {/* Growth Tracker Values */}
        {!isGrowthLoading && growthEntries.length > 0 && (
          <div className="flex justify-center gap-6 mt-6 w-full">
            {/* Weight */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1 mb-1">
                <Scale className="w-4 h-4 text-white/60" />
                <span className="text-white/60 text-xs">{t('dashboard:growth.weight')}</span>
              </div>
              <span className="text-white text-xl font-bold">
                {formatGrowthValue(latestGrowthValues.weight, 'weight').value}
              </span>
              <span className="text-white/60 text-xs">
                {formatGrowthValue(latestGrowthValues.weight, 'weight').unit}
              </span>
            </div>

            {/* Height */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1 mb-1">
                <Ruler className="w-4 h-4 text-white/60" />
                <span className="text-white/60 text-xs">{t('dashboard:growth.height')}</span>
              </div>
              <span className="text-white text-xl font-bold">
                {formatGrowthValue(latestGrowthValues.height, 'length').value}
              </span>
              <span className="text-white/60 text-xs">
                {formatGrowthValue(latestGrowthValues.height, 'length').unit}
              </span>
            </div>

            {/* Head Circumference */}
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1 mb-1">
                <Circle className="w-4 h-4 text-white/60" />
                <span className="text-white/60 text-xs">{t('dashboard:growth.head')}</span>
              </div>
              <span className="text-white text-xl font-bold">
                {formatGrowthValue(latestGrowthValues.head, 'length').value}
              </span>
              <span className="text-white/60 text-xs">
                {formatGrowthValue(latestGrowthValues.head, 'length').unit}
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Phase Info */}
      <section className="py-4">
        <PhaseInfo
          phase={phaseInfo?.phase ?? null}
          displayRange={phaseInfo?.phaseDisplayRange ?? ''}
          isLoading={phaseInfo?.isLoading}
        />
      </section>

      {/* Development Content (4 Category Tabs) */}
      <section>
        <DevelopmentContent
          categories={categories}
          content={content}
          isLoading={isContentLoading}
        />
      </section>

      {/* Milestones */}
      <section>
        <MilestoneChecklist
          milestones={milestones}
          babies={babies}
          onCheck={checkMilestone}
          onUncheck={uncheckMilestone}
          onUpdateNotes={updateNotes}
          isLoading={isMilestonesLoading}
          isUpdating={isUpdating}
        />
      </section>

      {/* Product Recommendation (current position) */}
      <RecommendationSlot placement="baby_development" position="after_milestones" />

      {/* Activities */}
      <section>
        <ActivitiesSection
          activities={activities}
          isLoading={isContentLoading}
        />
      </section>

      {/* Medical Info */}
      <section>
        <MedicalSection
          medical={medical}
          isLoading={isContentLoading}
        />
      </section>

      {/* Product Recommendation (end position) */}
      <RecommendationSlot placement="baby_development" position="end" />
    </div>
  );
}
